import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { parseSeatMapSvg } from '@bre-ticketing/seatmap/core';
import type { SvgImportResult } from '@bre-ticketing/seatmap/core';
import type { ApiError } from '../../api';
import { endpoints } from '../../api';
import { Button, ErrorNote, Panel } from '../../ui';

/**
 * Import a room from a drawing (D-38). The SVG is parsed here in the browser,
 * the operator sees what was found before anything is written, and then the
 * structure and the background go up as two requests.
 */
export function ImportSvg({
  manifestId,
  populated,
  onCancel,
  onDone,
  onError,
}: {
  manifestId: string;
  /** The draft already has sections; importing means replacing them. */
  populated: boolean;
  onCancel: () => void;
  onDone: () => void;
  onError: (error: ApiError) => void;
}) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<SvgImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const onFile = async (file: File | undefined) => {
    setParsed(null);
    setParseError(null);
    if (!file) return;
    setFileName(file.name);
    try {
      const text = await file.text();
      const result = parseSeatMapSvg(text);
      if (result.stats.seats === 0) {
        setParseError(result.warnings[0] ?? 'No seats were found in this drawing.');
        return;
      }
      setParsed(result);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Could not read this file');
    }
  };

  const run = useMutation({
    mutationFn: async () => {
      if (!parsed) return;
      await endpoints.importLayout(manifestId, parsed.layout, populated);
      /*
       * A drawing with nothing left in it clears the old one rather than
       * failing (D-182).
       *
       * The structure and the drawing go up as two requests, so before this the
       * second one refusing an empty body left the *previous* room showing
       * underneath the new seats — and replacing a background was the only
       * thing that had ever been possible, so there was no way back.
       */
      const drawing = parsed.background_svg.trim();
      if (drawing.length > 0) {
        await endpoints.setBackground(manifestId, drawing, 'image/svg+xml', fileName ?? 'seat-map.svg');
      } else {
        await endpoints.clearBackground(manifestId);
      }
    },
    onSuccess: onDone,
    onError,
  });

  return (
    <Panel
      title="Import a seat map drawing"
      description="An SVG whose groups are level → section → row (or section → table) and whose circles are seats, named by id — the Figma and Affinity export conventions both work. Exporting one floor rather than the whole room is fine. Everything else in the drawing becomes the background."
    >
      <div className="space-y-3 px-5 py-4">
        <input
          type="file"
          accept=".svg,image/svg+xml"
          onChange={(event) => void onFile(event.target.files?.[0])}
          className="text-sm"
          aria-label="Choose an SVG seat map"
        />
        {parseError ? <ErrorNote>{parseError}</ErrorNote> : null}
        {parsed ? (
          <div className="rounded border border-line bg-page/60 p-3 text-sm">
            <p className="font-medium text-ink-900">
              Found {parsed.stats.seats} seats in {parsed.stats.rows} rows
              {parsed.stats.tables > 0 ? ` and ${parsed.stats.tables} tables` : ''} across {parsed.stats.sections} sections
              {parsed.stats.levels > 0 ? ` on ${parsed.stats.levels} levels` : ''}; canvas {parsed.layout.canvas_width} × {parsed.layout.canvas_height}.
            </p>
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-600">
              {parsed.layout.sections.map((section) => {
                const tables = section.tables ?? [];
                const seats =
                  section.rows.reduce((n, row) => n + row.seats.length, 0) +
                  tables.reduce((n, table) => n + table.seats.length, 0);
                return (
                  <li key={section.code}>
                    <span className="font-medium text-ink-900">{section.code}</span> ·{' '}
                    {tables.length > 0 ? `${tables.length} tables` : `${section.rows.length} rows`} · {seats} seats
                  </li>
                );
              })}
            </ul>
            {parsed.warnings.length > 0 ? (
              <div className="mt-2 rounded border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
                <p className="font-semibold">Worth checking before importing:</p>
                <ul className="mt-1 list-disc pl-4">
                  {parsed.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {populated ? (
              <p className="mt-2 text-xs text-bad">This draft already has a layout. Importing replaces every section, row, seat and GA area in it.</p>
            ) : null}
          </div>
        ) : null}
        <div className="flex gap-2">
          <Button variant={populated ? 'danger' : 'primary'} disabled={!parsed || run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? 'Importing…' : populated ? 'Replace the layout with this drawing' : 'Import this drawing'}
          </Button>
          <Button onClick={onCancel} disabled={run.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </Panel>
  );
}
